"use strict"

import React from "react"
import PropTypes from "prop-types"
import { Button, Tab } from "react-bootstrap"
import memoize from "memoize-one"
const deepEqual = require("react-fast-compare")

import {
  getFormattedMessage,
  id_accessor,
  isLTPM,
  ltpm_run_navigator,
} from "../../../components/shared_components_registration"
import {
  non_observation_graphs,
  toggleSingleMultiRunPlot,
  graphsEqual,
  getRunBatonVarsPerPlotKey,
  getUnifiedPlotAxes,
  getRunTimeScales,
  getCrossPlotProps,
  getMaxTimeSpan,
  getSiteReactors,
} from "../../graphing/resources/utils"
import { AbstractBaseDashboard } from "../AbstractDashboards/AbstractDashboard"
import { deepcopy, equalArrays } from "../../utils"
import { RunErrorTabBody, RunLoadingTabBody, RunTabBody } from "./TabContent/RunTabs"
import { TabTitles } from "./TabContent/TabTitles"
import DefaultGraphConfigButton from "../../graphing/resources/DefaultGraphConfigButton"
import FavouritesSelector from "../../favourites/FavouritesSelector"
import ObservationSelectorPopper from "../../graphing/plotting/widgets/ObservationSelectorPopper"
import ToggleTimeExtractor from "../../graphing/plotting/widgets/ToggleTimeExtractor"
import ReportGenerationButton from "../../graphing/modals/ReportGenerationModal/ReportGenerationButton"
import { loadMultiRunPlots } from "../MultiRunView"

const { makePlotList, graph_sect_head } = ltpm_run_navigator

export default class LiveRunNavigator extends AbstractBaseDashboard {
  static defaultProps = {
    main_plot_settings: {
      height: 400,
      margin: {
        top: 30,
        bottom: 80,
        left: 75,
        right: 85,
      },
      smallWidthHeight: 312.5,
      titleFontSize: 16,
    },
    supplemental_plot_settings: {
      height: 250,
      margin: {
        top: 35,
        bottom: 60,
        left: 48,
        right: 52,
      },
      smallWidthHeight: 187.5,
      titleFontSize: 13,
    },
    chartWidthHeightChange: 700,
  }

  constructor(props) {
    super(props)

    this.state = {
      graph_variables: {
        graphs: "live_graphs",
        updateGraphs: "updateLiveGraphs",
      },
      no_live_run_key: "No Live Runs",
      width: 500,
      plotsPerRow: 2,
      activeGraphIdx: 0,
      activeGraph: {},
      activeRun: {},
      activeRunIdx: "",
      defaultRunLoaded: false,
      plotFactories: [],
      availableRuns: [],
      allObservationTypes: [],
      runBatons: [],
      plotList: [],

      runTagsLastUpd: {}, // UNIQUE
      runActionsLastUpd: {}, // UNIQUE
      checkForNewDataPulse: 600000, // milliseconds // UNIQUE
    }

    this.baseTimesByRun = {}

    this.toggleSingleMultiRunPlot = toggleSingleMultiRunPlot.bind(this)
    this.overlayRuns = this.overlayRuns.bind(this)
    this.manualReloadRun = this.manualReloadRun.bind(this)

    this.getChartWidths = this.getChartWidths.bind(this)
    this._getPlotAndScale = this._getPlotAndScale.bind(this)
    this.setActiveGraph = this.setActiveGraph.bind(this)
    this.setRunState = this.setRunState.bind(this)
    this.checkForRunUpdate = this.checkForRunUpdate.bind(this)

    this.getUpdatedRunsReloadActions = this.getUpdatedRunsReloadActions.bind(this)
    this.loadAvailableRunsAndObsTypes = this.loadAvailableRunsAndObsTypes.bind(this)
  }

  componentDidMount() {
    isLTPM && this.checkForRunUpdate()

    const plotFactories = this.getPlotFactories(
      this.props.live_graphs,
      this.props.updateLiveGraphs,
      this._plotFactoryCache,
    )
    this._plotFactoryCache = plotFactories

    this.setState({
      plotFactories: plotFactories,
      activeGraph: this.props.live_graphs[0],
    })

    this.mountResize()
  }

  componentWillUnmount() {
    // Commenting out until we resolve the performance issues on pulsed reload
    // if (isLTPM) {
    //   if (this.interval) clearInterval(this.interval)
    // }

    this.unmountResize()
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      runData = {},
      runs = [],
      live_graphs = [],
      updateLiveGraphs,
      special_graphs = [],
      queryTags = [],
      timeExtractor = {},
      live_run_cutoff,
      newActionRuns = [], // LTPM specific
    } = this.props
    const { plotFactories, activeRunIdx, activeGraphIdx, availableRuns, defaultRunLoaded } = this.state

    // Identify instance of a total run reload or targeting an actions/annotations
    // reload in order to update run variable held in parent and state
    const prevRunData = prevProps?.runData?.[activeRunIdx]
    const currRunData = runData?.[activeRunIdx]
    if (
      !deepEqual(prevRunData?.loading, currRunData?.loading) ||
      (isLTPM && !equalArrays(prevRunData?.actions, currRunData?.actions)) || // Can be added dynamically for LTPM
      (!isLTPM && !equalArrays(prevRunData?.annotations, currRunData?.annotations)) || // Can be added dynamically for LL
      (!isLTPM && !equalArrays(prevRunData?.runEventLabels, currRunData?.runEventLabels)) || // Can be added dynamically for LL
      (!isLTPM && !equalArrays(prevRunData?.fileList, currRunData?.fileList)) || // Run fileLists load in later for LL
      this.props?.show_actions !== prevProps?.show_actions ||
      this.props?.show_critical_actions !== prevProps?.show_critical_actions ||
      this.props?.show_site_actions !== prevProps?.show_site_actions ||
      this.props?.show_annotations !== prevProps?.show_annotations ||
      (!isLTPM && prevRunData?.labelingComplete !== currRunData?.labelingComplete)
    ) {
      this.setRunState(activeRunIdx)
    }

    const hasNewRuns = !equalArrays(runs, prevProps.runs)
    const hasRunDataChanges = !deepEqual(runData, prevProps.runData)
    const hasLiveGraphChanges = !deepEqual(live_graphs, prevProps.live_graphs)

    if ((hasLiveGraphChanges || hasNewRuns) && isLTPM) {
      this.checkForRunUpdate()
    }

    // Handle if placeholder show is toggled and currently selected run no longer matches show/hide status
    if (activeRunIdx && hasNewRuns && !runs.includes(parseInt(activeRunIdx)) && runs.length > 0) {
      this.setRunState(runs[0])
    }

    // There has been either a change in loaded run(s) or a change in run data itself
    const [newAvailableRuns, allObservationTypes] =
      hasNewRuns || hasRunDataChanges ? this.loadAvailableRunsAndObsTypes() : [null, null]
    const runList = newAvailableRuns || availableRuns

    runs.length > 0 &&
      loadMultiRunPlots(
        live_graphs,
        updateLiveGraphs,
        runData,
        hasNewRuns,
        prevProps,
        this.toggleSingleMultiRunPlot,
        "live_graphs",
        true,
        true,
      )

    // Make sure we have plotFactories for all graphs
    const newPlotFactories =
      hasLiveGraphChanges || live_graphs.length !== plotFactories.length
        ? this.getPlotFactories(live_graphs, updateLiveGraphs, this._plotFactoryCache)
        : null
    if (newPlotFactories) {
      this._plotFactoryCache = newPlotFactories
    }

    // Make sure activeGraph object in state contains all graphAdjustments made by user
    const activeGraph = !this.graphsEqual(live_graphs[activeGraphIdx], prevState.activeGraph)
      ? live_graphs[activeGraphIdx]
      : null

    // Make sure we have runBatons
    const runBatons =
      newAvailableRuns || newPlotFactories || !deepEqual(timeExtractor, prevProps.timeExtractor)
        ? getRunBatonVarsPerPlotKey(
            newPlotFactories || plotFactories,
            runList,
            timeExtractor,
            { ...this.props, ...this.state },
            this.state.runBatons,
          )
        : null

    // Get list of plots to make available in the graph selection popper
    let plotList
    if (
      hasLiveGraphChanges ||
      allObservationTypes ||
      !deepEqual(special_graphs, prevProps.special_graphs) ||
      !deepEqual(queryTags, prevProps.queryTags)
    ) {
      plotList = this.makePlotList(
        special_graphs.filter(
          (graph) => ![...["rnaseq"], ...non_observation_graphs].includes(graph?.special),
        ),
        queryTags,
        live_graphs,
        runList.map((r) => r.observationTypes || {}),
      )
      plotList = !deepEqual(plotList, this.state.plotList) ? plotList : null
    }

    // Will load runs/comments with updates - LTPM specific
    const runs_updated = this.getUpdatedRunsReloadActions(prevState)

    if (isLTPM) {
      // LTPM load
      if (!equalArrays(newActionRuns, prevProps.newActionRuns)) {
        // Will trigger a reload of run actions
        // Reload triggered by new action added by current user - Logic does NOT detect new actions added by other users
        newActionRuns.forEach((run_id) => {
          runData?.[run_id]?.requestActions && runData[run_id].requestActions()
        })
        this.props.setNewActionRuns && this.props.setNewActionRuns([])
      }

      const run_availability_update = !!allObservationTypes || hasNewRuns

      // Load Run/Tag Data
      if (live_run_cutoff !== prevProps.live_run_cutoff) {
        // Day cutoff requested has changed! Force reload and request tag data with new cutoff
        runList.forEach((run) => this.manualReloadRun(run))
      } else if (newPlotFactories || (runs_updated && runs_updated.length > 0) || run_availability_update) {
        const runs_not_updated = runs.filter((r) => !(runs_updated || []).includes(r))
        const factories = newPlotFactories ? newPlotFactories : plotFactories

        if (run_availability_update || newPlotFactories) {
          // Not forced reload - Only will load data for tags not already loaded
          this.ensureDataRequested(runs_not_updated, runData, factories, false)
          if (runs_updated && runs_updated.length > 0) {
            // Full data forced reload on all tags for runs with new data detected since last check
            this.ensureDataRequested(runs_updated, runData, factories, true)
          }
        } else {
          // Full data forced reload on all tags for runs with new data detected since last check
          this.ensureDataRequested(runs_updated, runData, factories, true)
        }
      }
    } else {
      // LanzaLake load
      if (live_run_cutoff !== prevProps.live_run_cutoff) {
        // Day cutoff requested has changed! Force reload and request tag data with new cutoff
        runList.forEach((run) => this.manualReloadRun(run))
      } else if (hasNewRuns || (runs && runData && (allObservationTypes || hasLiveGraphChanges))) {
        // A change in allObservationTypes indicates a change in loading status of any run
        this.ensureDataRequested(runs, runData, newPlotFactories || plotFactories, false)
      }
    }

    // Unified State Update
    if (newAvailableRuns || newPlotFactories || allObservationTypes || runBatons || plotList || activeGraph) {
      const newState = {}
      newAvailableRuns ? (newState["availableRuns"] = newAvailableRuns) : null
      newPlotFactories ? (newState["plotFactories"] = newPlotFactories) : null
      allObservationTypes ? (newState["allObservationTypes"] = allObservationTypes) : null
      runBatons ? (newState["runBatons"] = runBatons) : null
      plotList ? (newState["plotList"] = plotList) : null
      plotList && this.props.setPlotList && this.props.setPlotList(plotList)
      activeGraph ? (newState["activeGraph"] = activeGraph) : null
      this.setState({
        ...newState,
      })
    }

    if (!defaultRunLoaded) {
      const loaded_runs = []
      runs.forEach((run) => {
        if (runData[run] && !runData[run]?.loading) {
          loaded_runs.push(run)
        }
      })
      if (loaded_runs.length > 0) {
        // Commenting out until we resolve the performance issues on pulsed auto data reload
        // if (isLTPM) {
        //   this.interval = setInterval(this.checkForRunUpdate, this.state.checkForNewDataPulse)
        // }
        this.setRunState(loaded_runs[0])
      }
    }
  }

  loadAvailableRunsAndObsTypes() {
    const { runs, runData } = this.props
    const { availableRuns } = this.state
    let newAvailableRuns, allObservationTypes
    const filterRuns = (runArr) => (runArr || []).filter((r) => r.loading || r.err)
    newAvailableRuns = this.getAvailableRuns(runs, runData)
    newAvailableRuns = !deepEqual(newAvailableRuns, availableRuns) ? newAvailableRuns : null
    if (
      newAvailableRuns &&
      !deepEqual(filterRuns(newAvailableRuns), filterRuns(availableRuns)) &&
      newAvailableRuns.length !== filterRuns(newAvailableRuns).length
    ) {
      // Only need to grab allObsTypes on change of any run loading status.
      // That's the only time observationTypes will be reloaded on any of the run objects.
      allObservationTypes = this.makeObservationList(...newAvailableRuns.map((r) => r.observationTypes || {}))
    }
    return [newAvailableRuns, allObservationTypes]
  }

  // used in LTPM only
  checkForRunUpdate() {
    const updateState =
      this.props.checkForRunUpdate &&
      this.props.checkForRunUpdate(this.props.runs.join(), this.props.live_graphs, this.state)
    updateState && this.setState({ ...updateState })
  }

  getUpdatedRunsReloadActions(prevState) {
    /*
     *   Check for new data/actions for runs - Makes note of updated runs to reload later. Reloads actions
     *   now where applicable. Only works if this.interval is set-up to pulse.
     */
    const { runData } = this.props
    const { runTagsLastUpd, runActionsLastUpd } = this.state

    let runs_updated
    if (isLTPM && this.interval) {
      // Currently only meant to be a functionality on LTPM
      if (!deepEqual(runTagsLastUpd, prevState.runTagsLastUpd)) {
        runs_updated = this.checkIfVariableUpd(runTagsLastUpd, prevState.runTagsLastUpd)
      }

      if (!deepEqual(runActionsLastUpd, prevState.runActionsLastUpd)) {
        let actions_updated = this.checkIfVariableUpd(runActionsLastUpd, prevState.runActionsLastUpd)
        if (actions_updated.length > 0) {
          for (const runID of actions_updated) {
            const run = runData[runID]
            if (!run || run.err || !run.requestActions) {
              continue
            }
            run.requestActions()
          }
        }
      }
    }

    return runs_updated
  }

  checkIfVariableUpd(currVariable, prevVariable) {
    let info_updated = []
    for (const [key, value] of Object.entries(currVariable)) {
      if (Object.keys(prevVariable).includes(key) && value !== prevVariable[key]) {
        info_updated.push(parseInt(key))
      }
    }
    return info_updated
  }

  manualReloadRun(run) {
    // Reload Run Details/Observations
    this.props.reloadRun(run)
  }

  overlayRuns(op, index, plotEntry) {
    this.toggleSingleMultiRunPlot(index, op, plotEntry.plotConfigUpdater, op.special === "multi", true, true)
  }

  _getPlotAndScale(plotFactories, availableRuns, timeExtractor, alignment) {
    const viewerState = { ...this.props, ...this.state }
    const { runBatons } = viewerState

    const axesByRun = this._getRunPlotAxes(plotFactories, availableRuns, runBatons)
    const timeScalesByRun = getRunTimeScales(
      plotFactories,
      availableRuns,
      timeExtractor,
      viewerState,
      runBatons,
    )
    const crossPlotProps = getCrossPlotProps(plotFactories, availableRuns, runBatons)
    const maxTimeSpan = getMaxTimeSpan(timeScalesByRun, availableRuns, timeExtractor, alignment, true)
    const axesByPlot = getUnifiedPlotAxes(plotFactories, availableRuns, runBatons)

    return [axesByPlot, axesByRun, timeScalesByRun, crossPlotProps, maxTimeSpan]
  }

  /* Returns an object of each RunID mapped to a list of axes ranges per plot */
  _getRunPlotAxes(plotFactories, availableRuns, runBatons) {
    const runAxes = {}
    availableRuns.forEach((run) => {
      const run_id = run[id_accessor]
      const baton = runBatons[run_id]
      if (run && run_id && baton) {
        runAxes[run_id] = []
        for (const plotEntry of plotFactories) {
          const axes = baton.axesByKey[plotEntry.plotKey]
          runAxes[run_id].push(axes)
        }
      }
    })
    return runAxes
  }

  /* Get chart widths for main/supplemental plots */
  getChartWidths = memoize((width) => {
    const { main_plot_settings, supplemental_plot_settings } = this.props

    const chartWidth = Math.max(
      50,
      width - (main_plot_settings.margin.left + main_plot_settings.margin.right),
    )
    const suppChartWidth = Math.max(
      50,
      (width - (supplemental_plot_settings.margin.left + supplemental_plot_settings.margin.right)) /
        this.state.plotsPerRow -
        60,
    )

    return [chartWidth, suppChartWidth]
  })

  /* Set the active graph within a tab */
  setActiveGraph(graph_index, graph) {
    if (this.state.activeGraphIdx !== graph_index) {
      this.setState({
        activeGraphIdx: graph_index,
        activeGraph: graph,
      })
    }
  }

  setRunState(run) {
    const { runData } = this.props
    if (run) {
      const data = runData[run]
      this.props.setRunFocus && this.props.setRunFocus(data) // update variables in parent
      this.setState({
        // update state of this component
        activeRun: data,
        activeRunIdx: run.toString(),
        defaultRunLoaded: true,
      })
    }
  }

  /* Memoize functions to remember the last passed arguments and result.
   *  If a function is next called with the same arguments then the previous result is returned without
   *  rerunning the function unnecessarily.  Passing in deepEqual to check equality of more complex arguments. */
  makePlotList = memoize(makePlotList)
  getPlotAndScale = memoize(this._getPlotAndScale, deepEqual)
  graphsEqual = memoize(graphsEqual, deepEqual)

  render() {
    const {
      site,
      runs = [],
      timeExtractor = {},
      intl,
      live_graphs = [],
      updateLiveGraphs,
      minTime,
      maxTime,
      alignment = {},
      live_run_cutoff,
      zoomConfig,
      selected_timezone,
      main_plot_settings,
      supplemental_plot_settings,
      chartWidthHeightChange,
      performance_metrics_panel,
      report_metrics,
      RunFilteringWidgets,
      tagsLoading = false,
      leftSectionWidth = null,
      availableOnly = false,
      isQueryingRunIDs = true,
    } = this.props
    const getTextStr = (id, msg) => getFormattedMessage(id, msg, true, intl)
    const {
      plotFactories,
      availableRuns,
      allObservationTypes,
      defaultRunLoaded,
      activeGraphIdx,
      activeGraph,
      activeRun,
      activeRunIdx,
      width,
      runBatons,
      plotList,
      plotsPerRow,
      no_live_run_key,
    } = this.state
    const [chartWidth, suppChartWidth] = this.getChartWidths(leftSectionWidth ?? width)
    const activePlotList = plotList.filter((p) => (availableOnly ? !p.notAvailable : p))

    const [axesByPlot, axesByRun, timeScalesByRun, crossPlotProps, maxTimeSpan] = this.getPlotAndScale(
      plotFactories,
      availableRuns,
      timeExtractor,
      alignment,
    )
    const [xScales, suppXScales, xAxisParameters] = this.configureXAxisPerRun(
      availableRuns,
      timeScalesByRun,
      timeExtractor,
      maxTimeSpan,
      chartWidth,
      suppChartWidth,
      {},
      minTime,
      maxTime,
      alignment,
      (run_id) => this.state.runBatons[run_id],
      true,
    )

    const defaultActiveKey = runs.length === 0 ? no_live_run_key : `${activeRunIdx}`
    const runsLoading = availableRuns.filter((run) => run.loading).length > 0
    const viewGeneralRunToolbar =
      runs.length !== 0 && !runsLoading && availableRuns.filter((run) => run.err).length !== runs.length
    const siteReactors = getSiteReactors(this.props.runData, runs)

    return (
      <>
        {graph_sect_head(
          site,
          <>
            <RunFilteringWidgets runsLoading={runsLoading} tagsLoading={tagsLoading} />
            {viewGeneralRunToolbar && !isQueryingRunIDs && (
              <>
                <ReportGenerationButton
                  availableRuns={availableRuns}
                  plotList={plotList}
                  siteMetrics={report_metrics}
                  siteReactors={siteReactors}
                  intl={intl}
                />
                <ToggleTimeExtractor intl={intl} toggleAlignment={true} showLabel={false} />
                <ObservationSelectorPopper
                  placement={"bottom"}
                  options={activePlotList}
                  nameFn={(plot) => plot.title}
                  runFilters={siteReactors}
                  onSelect={(sel) => {
                    const new_graph = Object.assign({}, deepcopy(sel))
                    updateLiveGraphs((graphs) => [...graphs, new_graph])
                    this.setActiveGraph(live_graphs.length, new_graph)
                  }}
                >
                  <Button
                    title={getTextStr("graphing.add.new.graph", "Add New Graph")}
                    variant={"outline-primary"}
                  >
                    <span className={"fa fa-plus"} />
                  </Button>
                </ObservationSelectorPopper>
                <FavouritesSelector
                  view={"LTPM"}
                  graphSet={live_graphs}
                  loadGraphSet={updateLiveGraphs}
                  onSelect={() => this.setActiveGraph(0, live_graphs[0])}
                  intl={intl}
                  should_match_view={false}
                />
                <DefaultGraphConfigButton
                  intl={intl}
                  button={true}
                  type={"live_graphs"}
                  extra_logic={() => this.setActiveGraph(0, live_graphs[0])}
                />
              </>
            )}
          </>,
        )}
        <div ref={this.widthSensorRef} /> {/* Calc div width to set chart width properly */}
        {isQueryingRunIDs || (!activeRunIdx && runs.length > 0) ? (
          <RunLoadingTabBody />
        ) : (
          <Tab.Container
            defaultActiveKey={"first"}
            activeKey={defaultActiveKey}
            id="live-run-tabs"
            className={"full-height"}
            onSelect={(run) => {
              no_live_run_key !== defaultActiveKey && run && this.setRunState(run)
            }}
          >
            {/*
              Takes runs and renders a Tab for each run

              Under a Tab.Container, react-bootstrap requires a Tab.Content and Tab.Pane for each Tab
              and a Nav with Nav.Item and Nav.Link for each Tab.Pane
              and all Tab.Panes must have an eventKey that matches the Nav.Link eventKey
            */}
            <>
              <TabTitles
                availableRuns={availableRuns}
                defaultActiveKey={defaultActiveKey}
                defaultRunLoaded={defaultRunLoaded}
                intl={intl}
              />
              <Tab.Content>
                {availableRuns.length === 0 ? (
                  <Tab.Pane eventKey={no_live_run_key}>
                    <p style={{ marginTop: "45px", marginBottom: "45px", textAlign: "center" }}>
                      {getTextStr("livedash.no.active.runs", "There are no active runs in system")}
                    </p>
                  </Tab.Pane>
                ) : (
                  <>
                    {availableRuns.map((run) => {
                      const loading = run.loading,
                        allLoading = run.allLoading === undefined || run.allLoading,
                        run_id = run[id_accessor]

                      return (
                        <Tab.Pane key={`run${run_id}`} eventKey={`${run_id}`}>
                          <>
                            {run_id.toString() !== activeRunIdx ? (
                              <></>
                            ) : run.err ? (
                              <RunErrorTabBody run={run} manualReloadRun={this.manualReloadRun} intl={intl} />
                            ) : loading || allLoading || !activeRun ? (
                              <RunLoadingTabBody />
                            ) : (
                              <RunTabBody
                                site={site}
                                run={run}
                                availableRuns={availableRuns}
                                activeRun={activeRun}
                                activeGraph={Object.assign({}, activeGraph)}
                                activeGraphIdx={activeGraphIdx}
                                setActiveGraph={this.setActiveGraph}
                                plotList={activePlotList}
                                plotFactories={plotFactories}
                                baton={runBatons[run_id]}
                                x={xScales[run_id]}
                                supp_x={suppXScales[run_id]}
                                baseTime={this.baseTimesByRun[run_id]}
                                xParams={xAxisParameters[run_id]}
                                crossPlotProps={crossPlotProps}
                                allObservationTypes={allObservationTypes}
                                axesByPlot={axesByPlot}
                                axesByRun={null}
                                maxTimeSpan={maxTimeSpan}
                                updateLiveGraphs={updateLiveGraphs}
                                overlayRuns={this.overlayRuns}
                                cutoff={live_run_cutoff}
                                manualReloadRun={this.manualReloadRun}
                                forceReloadPlotData={this.forceReloadPlotData}
                                plotsPerRow={plotsPerRow}
                                zoomConfig={zoomConfig}
                                selected_timezone={selected_timezone}
                                chartWidth={chartWidth}
                                suppChartWidth={suppChartWidth}
                                chartWidthHeightChange={chartWidthHeightChange}
                                main_plot_settings={main_plot_settings}
                                supplemental_plot_settings={supplemental_plot_settings}
                                siteMetrics={performance_metrics_panel}
                                intl={intl}
                                tagsLoading={tagsLoading}
                                siteReactors={siteReactors}
                                availableOnly={this.props.availableOnly}
                              />
                            )}
                          </>
                        </Tab.Pane>
                      )
                    })}
                  </>
                )}
              </Tab.Content>
            </>
          </Tab.Container>
        )}
      </>
    )
  }
}

// initial prop types definition
LiveRunNavigator.propTypes = {
  urlPrefix: PropTypes.string,
  runs: PropTypes.arrayOf(PropTypes.number).isRequired,
  runData: PropTypes.object.isRequired,
  activeGraphIdx: PropTypes.number,
  // default props
  chartWidthHeightChange: PropTypes.number,
  main_plot_settings: PropTypes.object,
  supplemental_plot_settings: PropTypes.object,
  // app config
  config: PropTypes.object,
  selected_timezone: PropTypes.string,
  setSelectedTimezone: PropTypes.func,

  // others...
  site: PropTypes.string,
  alignment: PropTypes.object,
  colors: PropTypes.func,
  frequencyThreshold: PropTypes.number,
  graphs: PropTypes.arrayOf(PropTypes.object),
  live: PropTypes.bool,
  live_graphs: PropTypes.arrayOf(PropTypes.object),
  live_run_cutoff: PropTypes.number,
  maxTime: PropTypes.any,
  minTime: PropTypes.any,
  newActionRuns: PropTypes.any, // Unique to LTPM
  perRunTimeOffsets: PropTypes.object,
  performance_metrics_panel: PropTypes.array,
  report_metrics: PropTypes.array,
  plots: PropTypes.arrayOf(PropTypes.object),
  queryTags: PropTypes.arrayOf(PropTypes.object),
  reloadInterval: PropTypes.number,
  reloadRun: PropTypes.func,
  selectMutation: PropTypes.any,
  selectedMutation: PropTypes.any,
  setCutoff: PropTypes.func, // set days cutoff for live runs
  setFrequencyThreshold: PropTypes.func,
  setPlotList: PropTypes.func,
  setRunFocus: PropTypes.func,
  special_graphs: PropTypes.arrayOf(PropTypes.object),
  tagsLoading: PropTypes.bool,
  timeExtractor: PropTypes.object,
  timeExtractors: PropTypes.arrayOf(PropTypes.object),
  updateGraphs: PropTypes.func,
  updateLiveGraphs: PropTypes.func,
  updatePlots: PropTypes.func,
  updateSitePlots: PropTypes.func,
  updateTimeOffset: PropTypes.func,
  zoomConfig: PropTypes.object,
}
